import {Button, Form} from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import AsyncSelect from "react-select/async";
import React, {useMemo, useEffect, useState} from "react";
import { getProductList } from "../Controller/ProductController";
import {debounce} from "lodash";

const VerifyProductForm = ({ onSubmit, onCancel, initialQuantity, productData }) => {
    const [searchResults, setSearchResults] = useState([]);
    const [showSaveAsNew, setShowSaveAsNew] = useState(false);
    const [isSearched, setIsSearched] = useState(false);
    
    const {
        control,
        handleSubmit,
        setValue,
        trigger,
        formState: { errors },
    } = useForm({
        defaultValues: {
            // product: productData?.product_name ? {
            //     value: '',
            //     label: productData.product_name
            // } : null,
            quantity: initialQuantity ? initialQuantity.toString() : "0.01" // use initial quantity or default
        },
    });

    // Return options (no callback inside)
    const fetchProductOptions = async (inputValue) => {
        try {
            // console.log("check11")
            const response = await getProductList({ limit: 10, offset: 0, search: inputValue, store_id: localStorage.getItem('store_id') });
            if (response.success && response.data) {
                const results = response.data.map((product) => ({
                    value: product.slack,
                    label: product.name,
                    unitPrice: product.purchase_amount_excluding_tax,
                    tax: product?.TaxCode?.total_tax_percentage,
                }));
                
                setSearchResults(results);
                setShowSaveAsNew(results.length === 0 && inputValue.length > 0);
                setSearchResults(results.length === 0 ? [] : results);
                
                return results;
            }
            setSearchResults([]);
            if (inputValue.length > 0) {
                setShowSaveAsNew(true);
            }
            return [];
        } catch (e) {
            console.error('Error loading products:', e);
            setSearchResults([]);
            return [];
        }
    };

    // Auto-search with product name when component mounts
    useEffect(() => {
        console.log("VerifyProductForm mounted with productData:", productData);
        if (productData?.product_name) {
            // console.log("Auto-searching for product:", productData.product_name);
            setIsSearched(true);
            fetchProductOptions(productData.product_name).then((results) => {
                // console.log("Search results:", results);
                // Set the search results to the AsyncSelect
                if (results && results.length > 0) {
                    // You can optionally pre-select the first result
                    // setValue("product", results[0]);
                }else{
                    setSearchResults([]);

                }
            });
        } else {
            console.log("No productData.product_name found");
        }
    }, [productData?.product_name]);

// Memoize a debounced loader that *uses the callback form*
    const debouncedFetchProductOptions = useMemo(
        () =>
            debounce((inputValue, callback) => {
                fetchProductOptions(inputValue).then((opts) => callback(opts));
            }, 400), // tweak delay as needed
        []
    );

    const handleFormSubmit = (data) => {
        // Ensure quantity is a number before sending
        const formattedData = {
            ...data,
            quantity: parseFloat(data.quantity),
            saveAsNew: showSaveAsNew && !data.product,
        };
        onSubmit?.(formattedData);

        // Reset form fields after submit
        setValue("product", null);
        setValue("quantity", "");
    };

    const handleSaveAsNewClick = () => {
        setShowSaveAsNew(true);
        setValue("product", null);
        trigger("product"); // Clear validation error
    };
    return (
        <Form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
            {/* Product Search */}
            <Form.Group controlId="product" className="mb-3">
                <Form.Label>Merge <span style={{ fontStyle: 'italic', color: 'rgb(40, 167, 69)' }}>{productData?.product_name}</span> with existing product in store inventory</Form.Label>
                <Controller
                    control={control}
                    name="product"
                    rules={{
                        validate: (value) => {
                            if (showSaveAsNew) return true;
                            if (value) return true;
                            return "Please add at least one product";
                        }
                    }}
                    render={({field}) => (
                        <AsyncSelect
                            {...field}
                            placeholder={`Choose product...`}
                            cacheOptions={false}
                            defaultOptions={isSearched ? searchResults : true}
                            loadOptions={debouncedFetchProductOptions}
                            isClearable
                            value={field.value}
                            onChange={(option) => {
                                field.onChange(option);
                                setShowSaveAsNew(false);
                            }}
                        />
                    )}
                />
                {errors.product && !showSaveAsNew && (
                    <div className="invalid-feedback d-block">
                        {errors.product.message}
                    </div>
                )}
                
                {/* Show Save as New Item option when no products found */}
                {isSearched && searchResults.length === 0 && (
                    <div className="mt-2">
                        <div className="text-primary">
                            No Product Found
                        </div>
                        <small className="d-block text-muted mt-1 text-primary">
                            This will create a new product in the store inventory
                        </small>
                    </div>
                )}
            </Form.Group>

            {/* Quantity Input */}
            <Form.Group controlId="quantity" className="mb-3">
                <Form.Label>Accepted Quantity</Form.Label>
                <Controller
                    control={control}
                    name="quantity"
                    rules={{
                        required: 'Quantity is required',
                        validate: (v) => {
                            const n = typeof v === 'number' ? v : parseFloat(v);
                            return Number.isFinite(n) && n > 0 ? true : 'Enter a number > 0';
                        },
                    }}
                    render={({field}) => (
                        <Form.Control
                            {...field}
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0.01"
                            placeholder="Please enter accepted quantity"
                            isInvalid={!!errors.quantity}
                        />
                    )}
                />
                {errors.quantity && (
                    <div className="invalid-feedback d-block">
                        {errors.quantity.message}
                    </div>
                )}
            </Form.Group>

            <p className="text-muted mb-0">The accepted quantity will be added to the selected product and the same
                quantity will be reduced from the source store inventory.</p>

            {/* <p className={'text-muted mb-2 text-center'}>OR</p>

            <p className="text-primary mb-4 text-center">Add as a New Product in Store</p> */}

            <hr/>

            <div className={'d-flex justify-content-end'}>
                <Button
                    variant="outline-secondary"
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    type={'submit'}
                    className={'ms-2'}
                    variant="primary"
                >
                    Continue
                </Button>
            </div>
        </Form>
    );
};

export default VerifyProductForm;
